import { useEffect, useRef, useState } from "react";

interface LazyVimeoProps {
  src: string;
  title: string;
  className?: string;
  allow?: string;
  rootMargin?: string;
}

/**
 * Lazy-loads a Vimeo iframe only when it scrolls near the viewport.
 * This avoids loading ~300KB of Vimeo player JS on initial page load,
 * which significantly reduces Total Blocking Time and Max Potential FID.
 */
const LazyVimeo = ({
  src,
  title,
  className = "w-full h-full",
  allow = "autoplay; fullscreen; picture-in-picture",
  rootMargin = "300px",
}: LazyVimeoProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (shouldLoad) return;
    const node = containerRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldLoad, rootMargin]);

  return (
    <div ref={containerRef} className={className}>
      {shouldLoad ? (
        <iframe
          src={src}
          className="w-full h-full"
          allow={allow}
          allowFullScreen
          title={title}
          loading="lazy"
        />
      ) : (
        <div
          className="w-full h-full bg-black/40"
          aria-label={title}
          role="img"
        />
      )}
    </div>
  );
};

export default LazyVimeo;
